import React, { useState } from 'react';
import { HelpCircle, Copy, Check, Sparkles, CheckCircle2, ArrowRight, AlertTriangle, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../UI/Button';

export interface StepGuideProps {
  stepNumber: number;
  stepTitle: string;
  whyAreWeDoingThis: string;
  whatYouNeedBeforeStarting?: string[];
  exactlyWhatToDo: string[];
  whatToLookFor?: string[];
  whatToRecord?: string[];
  whatNotToRecord?: string[];
  lectureExample: {
    scenario: string;
    action: string;
    result: string;
  };
  doneWhen?: string;
  isStepValidated: boolean;
  validationMessage?: string;
  onContinue: () => void;
  onPrevious?: () => void;
  children: React.ReactNode;

  // Optional Controlled AI Assistance Helper
  aiPromptHelper?: {
    promptTitle: string;
    promptText: string;
    onImportCandidates: (
      pastedText: string
    ) => number | { success: boolean; count: number; error?: string } | boolean | void;
  };
}

export const KeywordStepGuide: React.FC<StepGuideProps> = ({
  stepNumber,
  stepTitle,
  whyAreWeDoingThis,
  exactlyWhatToDo,
  lectureExample,
  isStepValidated,
  validationMessage,
  onContinue,
  onPrevious,
  children,
  aiPromptHelper,
}) => {
  const [showGuidance, setShowGuidance] = useState(true);
  const [showAiModal, setShowAiModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [importStatus, setImportStatus] = useState('');

  const handleCopyPrompt = () => {
    if (!aiPromptHelper) return;
    navigator.clipboard.writeText(aiPromptHelper.promptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleRunImport = () => {
    if (!aiPromptHelper || !pastedText.trim()) {
      setImportStatus('⚠️ Please paste the AI response text before importing.');
      return;
    }

    try {
      const result = aiPromptHelper.onImportCandidates(pastedText);
      let count = 0;
      let success = false;
      let errorMsg = '';

      if (typeof result === 'number') {
        count = result;
        success = count > 0;
      } else if (typeof result === 'object' && result !== null) {
        count = result.count ?? (result.success ? 1 : 0);
        success = Boolean(result.success && count > 0);
        errorMsg = result.error || '';
      } else if (typeof result === 'boolean') {
        success = result;
        count = result ? 1 : 0;
      } else {
        // Fallback for void returns
        success = true;
        count = 1;
      }

      if (success && count > 0) {
        setImportStatus(`✓ Successfully imported ${count} candidate${count > 1 ? 's' : ''} into research dataset as "AI-assisted — Verification Required"!`);
        setPastedText('');
        setTimeout(() => {
          setImportStatus('');
          setShowAiModal(false);
        }, 2200);
      } else {
        setImportStatus(
          errorMsg || '⚠️ Could not detect any valid keyword candidates from the pasted text. Please make sure the output contains keywords and try again.'
        );
      }
    } catch (err: any) {
      setImportStatus(`⚠️ Error during candidate import: ${err?.message || 'Failed to parse text'}`);
    }
  };

  return (
    <div className="animate-fade-in" style={{ marginBottom: '20px' }}>
      {/* Compact Step Header */}
      <div
        style={{
          padding: '16px 20px',
          marginBottom: '14px',
          background: '#0b1220',
          borderLeft: '4px solid #38bdf8',
          borderTop: '1px solid rgba(56, 189, 248, 0.3)',
          borderRight: '1px solid #1e293b',
          borderBottom: '1px solid #1e293b',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '10px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>
            PHASE {stepNumber} OF 20
          </span>
          <h2 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>
            {stepTitle}
          </h2>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {aiPromptHelper && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Sparkles size={13} color="#38bdf8" />}
              onClick={() => setShowAiModal(true)}
            >
              AI Candidate Helper
            </Button>
          )}

          <button
            onClick={() => setShowGuidance(!showGuidance)}
            style={{
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              color: '#f59e0b',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <Lightbulb size={14} />
            <span>{showGuidance ? 'Hide Guidance' : 'Show Guidance'}</span>
            {showGuidance ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Collapsible Guidance Accordion Container */}
      {showGuidance && (
        <div style={{ padding: '18px', marginBottom: '14px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px' }}>
          {/* Why & Exact Steps in 2 Columns */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            {/* Why Are We Doing This? */}
            <div style={{ background: '#070a12', padding: '12px 14px', borderRadius: '8px', border: '1px solid #1e293b' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <HelpCircle size={13} />
                <span>Why Are We Doing This?</span>
              </div>
              <p style={{ fontSize: '12px', color: '#f8fafc', margin: 0, lineHeight: 1.5 }}>
                {whyAreWeDoingThis}
              </p>
            </div>

            {/* Baby Steps */}
            <div style={{ background: '#070a12', padding: '12px 14px', borderRadius: '8px', border: '1px solid #1e293b' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CheckCircle2 size={13} />
                <span>Step-by-Step Instructions</span>
              </div>
              <ul style={{ fontSize: '12px', color: '#f8fafc', margin: 0, paddingLeft: '16px', lineHeight: 1.5 }}>
                {exactlyWhatToDo.map((todo, idx) => (
                  <li key={idx} style={{ marginBottom: '2px' }}>{todo}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Practical Lecture Scenario Example */}
          <div style={{ background: '#070a12', padding: '12px 14px', borderRadius: '8px', border: '1px solid #1e293b' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', textTransform: 'uppercase', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Lightbulb size={13} />
              <span>Real Example — {lectureExample.scenario}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.4 }}>
              <strong>Action:</strong> {lectureExample.action} <span style={{ color: '#10b981', margin: '0 6px' }}>➔</span> <strong>Result:</strong> {lectureExample.result}
            </div>
          </div>
        </div>
      )}

      {/* Main Interactive Research Workstation / Step Body */}
      <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
        {children}
      </div>

      {/* Bottom Step Validation Footer */}
      <div style={{ background: '#070a12', border: '1px solid #1e293b', borderRadius: '12px', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          {isStepValidated ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#10b981', fontWeight: 700 }}>
              <CheckCircle2 size={16} />
              <span>{validationMessage || 'Phase requirements satisfied! Ready to proceed.'}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#f59e0b', fontWeight: 600 }}>
              <AlertTriangle size={16} />
              <span>{validationMessage || 'Complete the required research fields above to validate this phase.'}</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {onPrevious && stepNumber > 1 && (
            <Button variant="secondary" size="sm" onClick={onPrevious}>
              ← Previous Phase
            </Button>
          )}
          <Button variant="primary" size="sm" icon={<ArrowRight size={15} />} onClick={onContinue}>
            Complete & Continue →
          </Button>
        </div>
      </div>

      {/* Controlled AI Prompt Modal */}
      {showAiModal && aiPromptHelper && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', maxWidth: '580px', width: '100%', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 800, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={18} color="#38bdf8" />
              <span>{aiPromptHelper.promptTitle}</span>
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                Copy Master AI Prompt to ChatGPT / Gemini / Claude:
              </label>
              <div style={{ background: '#070a12', border: '1px solid #1e293b', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#ffffff', fontFamily: 'var(--font-mono)', maxHeight: '140px', overflowY: 'auto', marginBottom: '10px' }}>
                {aiPromptHelper.promptText}
              </div>
              <Button size="sm" variant={copied ? 'success' : 'secondary'} icon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={handleCopyPrompt}>
                {copied ? 'Prompt Copied!' : 'Copy Master Prompt'}
              </Button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                Paste AI Response Back Here to Import Candidates:
              </label>
              <textarea
                value={pastedText}
                onChange={(e) => {
                  setPastedText(e.target.value);
                  if (importStatus.startsWith('⚠️')) setImportStatus('');
                }}
                placeholder="Paste AI response here (bullet points, numbered lists, plain lines, markdown headings, tables, or JSON)..."
                rows={5}
                style={{ width: '100%', background: '#070a12', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px', color: '#ffffff', fontSize: '12px', outline: 'none' }}
              />
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                Tip: Works with ChatGPT, Claude, Gemini, DeepSeek in bulleted, numbered, markdown, table, or plain text formats.
              </div>
            </div>

            {importStatus && (
              <div
                style={{
                  fontSize: '12.5px',
                  color: importStatus.startsWith('✓') ? '#10b981' : '#f59e0b',
                  background: importStatus.startsWith('✓') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                  border: `1px solid ${importStatus.startsWith('✓') ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontWeight: 600,
                  marginBottom: '16px',
                  lineHeight: 1.4,
                }}
              >
                {importStatus}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button variant="secondary" size="sm" onClick={() => setShowAiModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleRunImport}>
                Import AI Candidates
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
